<template>
    <div id="setting-domain-name-page">
      <vx-card title="Domain Management">
        <!-- toolbar -->
        <div class="flex flex-wrap items-center mb-4">
          <vs-input
            v-model.trim="searchText"
            class="w-full md:w-1/3"
            icon-pack="feather"
            icon="icon-search"
            placeholder="Search domain..."
          />
  
          <div class="ml-auto mt-3 md:mt-0">
            <vs-button
              color="primary"
              icon-pack="feather"
              icon="icon-rotate-cw"
              @click="refreshData"
            >
              Refresh
            </vs-button>
          </div>
        </div>

        <div class="mb-4 p-3 border border-primary rounded">
          <div class="font-semibold mb-2 text-primary">สถานะโดเมน</div>
          <div class="text-sm">
            <div class="mb-1"><span class="font-semibold">Active</span> = โดเมนที่เปิดใช้งานอยู่ตอนนี้</div>
            <div class="mb-1"><span class="font-semibold">Used</span> = โดเมนที่เคยใช้งานแล้ว (ปัจจุบันไม่ได้ Active)</div>
            <div class="mb-1"><span class="font-semibold">Unused</span> = โดเมนที่ยังไม่ถูกใช้งาน</div>
            <div><span class="font-semibold">Process</span> = โดเมนที่กำลังดำเนินการ/รอจดโดเมน</div>
          </div>
        </div>
  
        <!-- table -->
        <vs-table stripe pagination max-items="10" :data="filteredDomains">
          <template slot="thead">
            <vs-th>Domain</vs-th>
            <vs-th>Status</vs-th>
            <vs-th>Last Activated</vs-th>
            <vs-th>Action</vs-th>
          </template>
  
          <template slot-scope="{ data }">
            <vs-tr :key="tr.id || indextr" v-for="(tr, indextr) in data">
              <vs-td :data="tr.domain">
                <a
                  v-if="tr.status === 'active'"
                  :href="getDomainHref(tr.domain)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="font-semibold text-primary"
                >
                  {{ tr.domain }}
                </a>
                <span v-else class="font-semibold">{{ tr.domain }}</span>
                <div class="text-sm text-grey mt-1">
                  redirect to: {{ tr.redirectTo || '-' }}
                </div>  
              </vs-td>
  
              <vs-td :data="tr.status">
                <vs-chip :color="statusColor(tr.status)">
                  {{ statusLabel(tr.status) }}
                </vs-chip>
              </vs-td>
  
              <vs-td :data="tr.lastActivated">
                {{ formatDateTime(tr.lastActivated) }}
              </vs-td>
  
              <vs-td>
                <div class="flex">
                  <vs-button
                    v-if="tr.status !== 'process'"
                    type="border"
                    size="small"
                    :color="actionColor(tr.status)"
                    @click="toggleStatus(tr)"
                  >
                    {{ actionLabel(tr.status) }}
                  </vs-button>
                  <span v-else class="text-grey">-</span>
                </div>
              </vs-td>
            </vs-tr>
          </template>
        </vs-table>
  
        <div v-if="!loading && filteredDomains.length === 0" class="text-center py-6 text-grey">
          ไม่พบข้อมูลโดเมน
        </div>
      </vx-card>
    </div>
  </template>
  
<script>
import axios from '../../axios'
  
export default {
  name: 'SettingDomainName',
  data () {
    return {
      loading: false,
      searchText: '',
      domains: []
    }
  },
  computed: {
    canAction () {
      const appUser = this.$store && this.$store.state && this.$store.state.AppActiveUser
      const permissions = appUser && appUser.permissions
      if (!permissions || !permissions.setDomainName) return true
      return !!permissions.setDomainName.action
    },
    filteredDomains () {
      const q = (this.searchText || '').toLowerCase()
      if (!q) return this.domains
  
      return this.domains.filter(item => {
        const domain = (item.domain || '').toLowerCase()
        const status = (item.status || '').toLowerCase()
        return domain.includes(q) || status.includes(q)
      })
    }
  },
  async mounted () {
    await this.fetchDomains()
  },
  methods: {
    async fetchDomains () {
      this.loading = true
      try {
        const res = await axios.get('/settingdomains/list')
        const list = Array.isArray(res.data) ? res.data : []
        this.domains = list.length > 0 ? this.mapApiToView(list) : []
      } catch (error) {
        this.domains = []
      } finally {
        this.loading = false
      }
    },
    mapApiToView (list) {
      return list.map(item => ({
        id: item.id,
        domain: item.domain_name || item.domain || item.name || '-',
        redirectTo: item.redirect_to || item.redirect_domain || item.target_domain || item.destination_domain || null,
        status: this.normalizeStatus(item.status, item.is_active),
        lastActivated: item.last_activated_at || item.last_activated || item.lastActivated || item.updated_at || item.created_at || null
      }))
    },
    normalizeStatus (status, isActive) {
      if (status === 'active' || status === 'used' || status === 'unused' || status === 'process') return status
      if (isActive === 1 || isActive === true) return 'active'
      return 'unused'
    },
    statusLabel (status) {
      if (status === 'active') return ' Active'
      if (status === 'used') return ' Used'
      if (status === 'process') return ' Process'
      return ' Unused'
    },
    statusColor (status) {
      if (status === 'active') return 'success'
      if (status === 'used') return 'warning'
      if (status === 'process') return 'primary'
      return 'dark'
    },
    actionLabel (status) {
      if (status === 'active') return 'Disable'
      if (status === 'process') return 'Mark Active'
      return 'Activate'
    },
    actionColor (status) {
      if (status === 'active') return 'danger'
      if (status === 'process') return 'primary'
      return 'success'
    },
    formatDateTime (value) {
      if (!value) return '-'
      if (typeof value === 'string') {
        const normalized = value.replace('T', ' ').replace('Z', '')
        if (normalized.length >= 19) return normalized.slice(0, 19)
      }
      const date = new Date(value)
      if (isNaN(date.getTime())) return value
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      const hh = String(date.getHours()).padStart(2, '0')
      const mm = String(date.getMinutes()).padStart(2, '0')
      const ss = String(date.getSeconds()).padStart(2, '0')
      return `${y}-${m}-${d} ${hh}:${mm}:${ss}`
    },
    getDomainHref (domain) {
      const value = (domain || '').trim()
      if (!value) return '#'
      if (value.indexOf('http://') === 0 || value.indexOf('https://') === 0) return value
      return `https://${value}`
    },
    refreshData () {
      this.fetchDomains()
    },
    async toggleStatus (row) {
      let nextStatus = 'process'
      if (row.status === 'active') nextStatus = 'unused'
      else if (row.status === 'process') nextStatus = 'active'

      // Update UI immediately for mockup/demo flow.
      row.status = nextStatus
      if (nextStatus === 'active') row.lastActivated = this.todayString()
      try {
        await axios.post('setting/domain/update-status', {
          id: row.id,
          status: nextStatus
        })
      } catch (error) {
        this.$vs.notify({
          color: 'warning',
          position: 'top-right',
          title: 'Mockup mode',
          text: 'อัปเดตในหน้าจอแล้ว แต่ API ยังไม่พร้อมใช้งาน'
        })
      }
    },
    todayString () {
      const now = new Date()
      const y = now.getFullYear()
      const m = String(now.getMonth() + 1).padStart(2, '0')
      const d = String(now.getDate()).padStart(2, '0')
      const hh = String(now.getHours()).padStart(2, '0')
      const mm = String(now.getMinutes()).padStart(2, '0')
      const ss = String(now.getSeconds()).padStart(2, '0')
      return `${y}-${m}-${d} ${hh}:${mm}:${ss}`
    }
  }
}
</script>